<!-- components/DayPickerPopup.vue - VERSIONE MIGLIORATA -->
<template>
	<div
		v-if="isOpen"
		class="date-picker-overlay"
		@click="closeOnOverlay"
	>
		<div
			class="date-picker-modal"
			@click.stop
		>
			<div class="date-picker-header">
				<h4>Seleziona Data e Ora di Inizio</h4>
				<button
					class="close-btn"
					@click="close"
				>
					<XIcon size="20" />
				</button>
			</div>

			<div class="date-picker-body">
				<!-- Selezione data -->
				<div class="date-section">
					<label>Data di inizio:</label>
					<div class="date-input-wrapper">
						<CalendarIcon
							class="calendar-icon"
							size="18"
						/>
						<input
							type="date"
							v-model="selectedDate"
							:min="minDate"
							:max="maxDate"
						/>
					</div>
				</div>

				<!-- Selezione ora -->
				<div class="time-section">
					<label>Ora di inizio (24h):</label>
					<div class="time-inputs">
						<div class="time-select-wrapper">
							<select v-model="selectedHour">
								<option
									v-for="h in 24"
									:key="h"
									:value="h - 1"
								>
									{{ (h - 1).toString().padStart(2, "0") }}
								</option>
							</select>
							<ChevronDownIcon
								class="select-chevron"
								size="16"
							/>
						</div>
						<span class="time-separator">:</span>
						<div class="time-select-wrapper">
							<select v-model="selectedMinute">
								<option
									v-for="m in [0, 15, 30, 45]"
									:key="m"
									:value="m"
								>
									{{ m.toString().padStart(2, "0") }}
								</option>
							</select>
							<ChevronDownIcon
								class="select-chevron"
								size="16"
							/>
						</div>
					</div>
				</div>

				<!-- Selezione fine -->
				<div class="date-section">
					<label>Data di fine:</label>
					<div class="date-input-wrapper">
						<CalendarIcon
							class="calendar-icon"
							size="18"
						/>
						<input
							type="date"
							v-model="selectedEndDate"
							:min="minEndDate"
							:max="maxEndDate"
						/>
					</div>
				</div>

				<div class="time-section">
					<label>Ora di fine (24h):</label>
					<div class="time-inputs">
						<div class="time-select-wrapper">
							<select v-model="selectedEndHour">
								<option
									v-for="h in 24"
									:key="h"
									:value="h - 1"
								>
									{{ (h - 1).toString().padStart(2, "0") }}
								</option>
							</select>
							<ChevronDownIcon
								class="select-chevron"
								size="16"
							/>
						</div>
						<span class="time-separator">:</span>
						<div class="time-select-wrapper">
							<select v-model="selectedEndMinute">
								<option
									v-for="m in [0, 15, 30, 45]"
									:key="m"
									:value="m"
								>
									{{ m.toString().padStart(2, "0") }}
								</option>
							</select>
							<ChevronDownIcon
								class="select-chevron"
								size="16"
							/>
						</div>
					</div>
				</div>

				<!-- Anteprima -->
				<div class="preview-section">
					<div class="preview-title">Visualizzerai i dati:</div>
					<div class="preview-dates">
						<div class="preview-row"><strong>Da:</strong> {{ formatDate(selectedDateTime) }}</div>
						<div class="preview-row">
							<strong>A:</strong>
							{{ formatDate(selectedEndDateTime) }}
							<span v-if="selectedEndDateTime >= currentDateTime">(ora attuale)</span>
						</div>
					</div>
				</div>
			</div>

			<div class="date-picker-footer">
				<button
					class="cancel-btn"
					@click="close"
				>
					Annulla
				</button>
				<button
					class="apply-btn"
					@click="apply"
				>
					Applica
				</button>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ArrowRight as ArrowRightIcon, Calendar as CalendarIcon, ChevronDown as ChevronDownIcon, X as XIcon } from "lucide-vue-next";
	import { computed, ref, watch } from "vue";

	const props = defineProps({
		isOpen: Boolean,
	});

	const emit = defineEmits(["close", "apply"]);

	const selectedDate = ref("");
	const selectedHour = ref(0);
	const selectedMinute = ref(0);

	const selectedEndDate = ref("");
	const selectedEndHour = ref(0);
	const selectedEndMinute = ref(0);

	const isAutoAdjustingEndTime = ref(false);

	// Imposta data di default a 7 giorni fa
	const today = new Date();
	const sevenDaysAgo = new Date();
	sevenDaysAgo.setDate(today.getDate() - 7);
	sevenDaysAgo.setHours(0, 0, 0, 0);
	selectedDate.value = sevenDaysAgo.toISOString().split("T")[0];

	// imposta data di fine di default a oggi
	selectedEndDate.value = today.toISOString().split("T")[0];
	selectedEndHour.value = today.getHours();
	selectedEndMinute.value = Math.floor(today.getMinutes() / 15) * 15;

	const selectedDateTime = computed(() => {
		if (!selectedDate.value) return null;

		const date = new Date(selectedDate.value);
		date.setHours(selectedHour.value, selectedMinute.value, 0, 0);
		return date;
	});

	const selectedEndDateTime = computed(() => {
		if (!selectedEndDate.value) return null;

		const date = new Date(selectedEndDate.value);
		date.setHours(selectedEndHour.value, selectedEndMinute.value, 0, 0);
		return date;
	});

	const currentDateTime = computed(() => {
		return new Date(); // Ora attuale
	});

	// Limiti per la data
	const minDate = computed(() => {
		const date = new Date();
		date.setFullYear(date.getFullYear() - 1); // Massimo 1 anno fa
		return date.toISOString().split("T")[0];
	});

	const maxDate = computed(() => {
		const date = new Date();
		return date.toISOString().split("T")[0]; // Non può selezionare date future
	});

	const maxEndDate = computed(() => {
		return new Date().toISOString().split("T")[0]; // oggi
	});

	const minEndDate = computed(() => {
		return selectedDate.value || minDate.value;
	});

	function formatDate(date) {
		if (!date) return "";
		return date.toLocaleString("it-IT", {
			weekday: "short",
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	function formatDateShort(date) {
		if (!date) return "";
		return date.toLocaleString("it-IT", {
			day: "2-digit",
			month: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	}

	function close() {
		emit("close");
	}

	function closeOnOverlay(event) {
		if (event.target.classList.contains("date-picker-overlay")) {
			close();
		}
	}

	function apply() {
		if (selectedDateTime.value && selectedEndDateTime.value) {
			emit("apply", {
				start: selectedDateTime.value,
				end: selectedEndDateTime.value,
				label: `${formatDateShort(selectedDateTime.value)} → ${formatDateShort(selectedEndDateTime.value)}`,
			});
		}
		close();
	}

	// Reset quando si apre
	watch(
		() => props.isOpen,
		(isOpen) => {
			if (!isOpen) return;

			const sevenDaysAgo = new Date();
			sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
			sevenDaysAgo.setHours(0, 0, 0, 0);

			const now = new Date();

			selectedDate.value = sevenDaysAgo.toISOString().split("T")[0];
			selectedHour.value = 0;
			selectedMinute.value = 0;

			selectedEndDate.value = now.toISOString().split("T")[0];
			selectedEndHour.value = now.getHours();
			selectedEndMinute.value = Math.floor(now.getMinutes() / 15) * 15;
		}
	);

	// validazione start/ end
	watch([selectedDateTime, selectedEndDateTime], ([start, end]) => {
		if (!start || !end) return;

		const now = new Date();

		// Fine non può superare ora attuale
		if (end > now) {
			selectedEndDate.value = maxEndDate.value;
			selectedEndHour.value = now.getHours();
			selectedEndMinute.value = Math.floor(now.getMinutes() / 15) * 15;
			return;
		}

		// Fine non può essere prima dell'inizio
		if (end < start) {
			selectedEndDate.value = selectedDate.value;
			selectedEndHour.value = selectedHour.value;
			selectedEndMinute.value = selectedMinute.value;
		}
	});

	// Aggiorna ora/minuti se la data di fine è oggi
	watch(
		() => selectedEndDate.value,
		(endDate) => {
			if (!endDate) return;

			const today = new Date();
			const todayStr = today.toISOString().split("T")[0];

			// Caso 1: fine = oggi → ora attuale
			if (endDate === todayStr) {
				selectedEndHour.value = today.getHours();
				selectedEndMinute.value = Math.floor(today.getMinutes() / 15) * 15;
				return;
			}

			// Caso 2: fine ≠ oggi → mezzanotte
			selectedEndHour.value = 0;
			selectedEndMinute.value = 0;
		}
	);

	// evitiamo selezione di date future quando si seleziona la data di fine ad oggi
	watch(
		() => [selectedEndDate.value, selectedEndHour.value, selectedEndMinute.value],
		([date, h, m]) => {
			if (isAutoAdjustingEndTime.value) return;
			if (!date) return;

			const now = new Date();
			const todayStr = now.toISOString().split("T")[0];

			// Applichiamo il clamp solo se la data è oggi
			if (date !== todayStr) return;

			// Costruisco datetime selezionato (oggi + h:m)
			const selected = new Date(date);
			selected.setHours(Number(h), Number(m), 0, 0);

			// Se selezionato > now → clamp a now (arrotondato a 15')
			if (selected.getTime() > now.getTime()) {
				isAutoAdjustingEndTime.value = true;

				selectedEndHour.value = now.getHours();
				selectedEndMinute.value = Math.floor(now.getMinutes() / 15) * 15;

				// rilascio guard nel microtask successivo
				queueMicrotask(() => {
					isAutoAdjustingEndTime.value = false;
				});
			}
		},
		{ flush: "sync" }
	);
</script>

<style scoped>
	/* Forza colore uniforme su input date (Chromium) */
	input[type="date"] {
		color: #fff;
		color-scheme: dark;
	}

	/* Override dei segmenti interni */
	input[type="date"]::-webkit-datetime-edit,
	input[type="date"]::-webkit-datetime-edit-text,
	input[type="date"]::-webkit-datetime-edit-month-field,
	input[type="date"]::-webkit-datetime-edit-day-field,
	input[type="date"]::-webkit-datetime-edit-year-field {
		color: #fff;
		opacity: 1;
	}

	/* Evita grigiatura quando non focused */
	input[type="date"]::-webkit-datetime-edit-fields-wrapper {
		color: #fff;
	}

	.date-picker-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0, 0, 0, 0.7);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1000;
	}

	.date-picker-modal {
		background: #2d2d2d;
		border-radius: 8px;
		width: 420px;
		max-width: 90vw;
		max-height: 90vh;
		display: flex;
		flex-direction: column;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
		overflow: hidden;
	}

	.date-picker-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 16px 20px;
		background: #3d3d3d;
		border-bottom: 1px solid #444;
	}

	.date-picker-header h4 {
		margin: 0;
		color: white;
		font-size: 1.1rem;
	}

	.close-btn {
		background: none;
		border: 0;
		cursor: pointer;
		padding: 4px;
		color: #fff;
		transition: color 0.15s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.close-btn:hover {
		color: #008f8f;
	}

	.date-picker-body {
		padding: 20px;
		overflow-y: auto;
		flex: 1;
	}

	.date-section,
	.time-section {
		margin-bottom: 20px;
	}

	.date-section label,
	.time-section label {
		display: block;
		margin-bottom: 8px;
		color: #ddd;
		font-weight: 500;
		font-size: 0.95rem;
	}

	/* Stile per input data con icona */
	.date-input-wrapper {
		position: relative;
		width: 100%;
	}

	.date-input-wrapper .calendar-icon {
		position: absolute;
		left: 12px;
		top: 50%;
		transform: translateY(-50%);
		color: #aaa;
		pointer-events: none;
		transition: color 0.15s;
	}

	.date-input-wrapper:hover .calendar-icon {
		color: #008f8f;
	}

	.date-input-wrapper input[type="date"] {
		width: 100%;
		padding: 10px 10px 10px 40px;
		background: #1a1a1a;
		border: 1px solid #444;
		border-radius: 4px;
		color: white;
		font-size: 1rem;
		font-family: inherit;
		transition: border-color 0.15s;
	}

	.date-input-wrapper input[type="date"]:focus {
		outline: none;
		border-color: #008f8f;
	}

	.date-input-wrapper input[type="date"]:focus + .calendar-icon {
		color: #008f8f;
	}

	/* Personalizza il calendario nativo */
	.date-input-wrapper input[type="date"]::-webkit-calendar-picker-indicator {
		background: transparent;
		color: transparent;
		cursor: pointer;
		height: 100%;
		width: 100%;
		position: absolute;
		left: 0;
		top: 0;
		opacity: 0;
	}

	/* Stile per selettori ora/minuti */
	.time-inputs {
		display: flex;
		align-items: center;
		gap: 12px;
	}

	.time-select-wrapper {
		position: relative;
		flex: 1;
	}

	.time-select-wrapper select {
		width: 100%;
		padding: 10px 36px 10px 12px;
		background: #1a1a1a;
		border: 1px solid #444;
		border-radius: 4px;
		color: white;
		font-size: 1rem;
		font-family: inherit;
		appearance: none;
		cursor: pointer;
		transition: border-color 0.15s;
	}

	.time-select-wrapper select:focus {
		outline: none;
		border-color: #008f8f;
	}

	.select-chevron {
		position: absolute;
		right: 12px;
		top: 50%;
		transform: translateY(-50%);
		color: #aaa;
		pointer-events: none;
		transition: color 0.15s;
	}

	.time-select-wrapper select:focus + .select-chevron {
		color: #008f8f;
	}

	.time-select-wrapper select:hover + .select-chevron {
		color: #008f8f;
	}

	.time-separator {
		color: #aaa;
		font-weight: 500;
		padding: 0 4px;
	}

	.preview-section {
		margin-top: 24px;
		padding: 16px;
		background: rgba(0, 0, 0, 0.2);
		border-radius: 6px;
		border-left: 4px solid #008f8f;
	}

	.preview-title {
		color: #aaa;
		font-size: 0.9rem;
		margin-bottom: 12px;
		line-height: 1.4;
	}

	.preview-dates {
		color: white;
		font-size: 0.95rem;
	}

	.preview-row {
		margin-bottom: 8px;
		line-height: 1.5;
	}

	.preview-row strong {
		color: #ccc;
		font-weight: 600;
		margin-right: 4px;
	}

	.range-info {
		margin-top: 16px;
		padding: 12px;
		background: rgba(0, 0, 0, 0.3);
		border-radius: 4px;
	}

	.range-display {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		color: #ddd;
		font-size: 0.9rem;
	}

	.range-from,
	.range-to {
		padding: 6px 12px;
		background: rgba(255, 255, 255, 0.05);
		border-radius: 4px;
		font-weight: 600;
		color: #fff;
	}

	/* .range-to {
		font-weight: 600;
		color: #fff;
	} */

	.range-arrow {
		color: #008f8f;
		flex-shrink: 0;
	}

	.date-picker-footer {
		display: flex;
		justify-content: flex-end;
		gap: 12px;
		padding: 16px 20px;
		background: #3d3d3d;
		border-top: 1px solid #444;
	}

	.cancel-btn,
	.apply-btn {
		padding: 10px 20px;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-weight: 500;
		font-size: 0.9rem;
		transition: background-color 0.2s;
		font-family: inherit;
	}

	.cancel-btn {
		background: #555;
		color: white;
	}

	.cancel-btn:hover {
		background: #666;
	}

	.apply-btn {
		background: #008f8f;
		color: white;
	}

	.apply-btn:hover {
		background: #00a7a7;
	}

	@media (max-width: 480px) {
		.date-picker-modal {
			width: 92vw;
			max-width: 92vw;
			max-height: 95vh;
			margin: 0 auto;
		}

		.date-picker-header {
			padding: 12px 16px;
		}

		.date-picker-header h4 {
			font-size: 1rem;
		}

		.date-picker-body {
			padding: 12px;
		}

		.date-section,
		.time-section {
			margin-bottom: 16px;
		}

		.time-inputs {
			flex-direction: row; /* Mantieni row se possibile o gestisci bene */
			align-items: center;
			gap: 8px;
		}

		.time-select-wrapper select {
			padding: 8px 30px 8px 10px;
			font-size: 0.9rem;
		}

		.time-separator {
			display: block;
			padding: 0 2px;
		}

		.date-input-wrapper input[type="date"] {
			padding: 8px 8px 8px 36px;
			font-size: 0.9rem;
		}

		.preview-section {
			margin-top: 16px;
			padding: 12px;
		}

		.preview-row {
			font-size: 0.85rem;
		}

		.date-picker-footer {
			padding: 10px 12px;
			gap: 8px;
		}

		.cancel-btn,
		.apply-btn {
			padding: 8px 12px;
			flex: 1;
		}
	}
</style>
